import { AccountSettingsDialog } from "@/components/AccountSettingsDialog";
import { BooLogo } from "@/components/BooLogo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar-context";
import { api, type OrgRole } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";
import { limitsOnly } from "@/lib/usage-tabs";
import { useIsSuperAdmin } from "@/lib/use-superadmin-view";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

type SessionUser = {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  role?: string | null;
};
import {
  BarChart3,
  Blocks,
  Cable,
  Check,
  ChevronRight,
  ChevronsUpDown,
  Container,
  Cpu,
  Loader2,
  LogOut,
  Plus,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useState } from "react";

// The dashboard is container-centric: the home page lists containers and
// clicking one opens its dedicated tab strip (Tasks/Knowledge/Logs/Usage/
// Integrations). So Tasks & Knowledge are no longer top-level — they only
// exist inside a container. Org-level Usage stays as a rollup.
// Account Settings is reachable from the bottom user menu, so it doesn't
// need a sidebar entry — that slot is reused for org-level settings.
// `to` is the Tanstack route template — params are filled with the active
// orgId at render time. Keeping the templates here (not the resolved URLs)
// means Tanstack stays in charge of type-safety end-to-end.
// `orgSection` items sit in the collapsible Organization section below the main three.
const NAV_MAIN = [
  { to: "/orgs/$orgId", label: "Boos", icon: Container, permissionKey: null, orgSection: false },
  // Usage holds billing too; members see it for their own spending limits (canViewSpendLimits).
  { to: "/orgs/$orgId/usage", label: "Usage", icon: BarChart3, permissionKey: ["canViewUsage", "canViewBilling", "canViewSpendLimits"] as const, orgSection: false },
  { to: "/orgs/$orgId/members", label: "Admins", icon: Users, permissionKey: null, orgSection: true },
  { to: "/orgs/$orgId/access", label: "Boo Org", icon: ShieldCheck, permissionKey: "canViewAccess" as const, orgSection: true },
  // `exact`: shown only once the backend says yes (older backends don't send canViewAudit).
  { to: "/orgs/$orgId/audit", label: "Audit log", icon: ScrollText, permissionKey: "canViewAudit" as const, exact: true, orgSection: true },
  { to: "/orgs/$orgId/connections", label: "Connections", icon: Cable, permissionKey: null, orgSection: true },
  { to: "/orgs/$orgId/integrations", label: "Integrations", icon: Blocks, permissionKey: null, orgSection: true },
  { to: "/orgs/$orgId/models", label: "Models", icon: Cpu, permissionKey: null, orgSection: true },
  { to: "/orgs/$orgId/settings", label: "Settings", icon: Settings, permissionKey: null, orgSection: true },
] as const;

// `label` is widened: the Usage entry is relabeled for viewers who only see Spending limits.
type NavItem = Omit<(typeof NAV_MAIN)[number], "label"> & { label: string };

function OrgSwitcher() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");

  const { data: session } = useSession();

  const orgsQuery = useQuery({
    queryKey: ["orgs"],
    queryFn: api.orgs.list,
  });
  const orgs = orgsQuery.data ?? [];

  const activeOrgId = session?.session.activeOrganizationId ?? null;
  const activeOrg = orgs.find((o) => o.id === activeOrgId);

  // An active org that isn't in the member's own org list can only happen for a
  // super-admin viewing another org — a normal user with a non-member active
  // org is rejected server-side (resolveSession 403). So this condition alone
  // identifies the cross-org super-admin view without depending on the role
  // string. Wait for the org list to actually load before deciding.
  const viewingForeignOrg =
    !!activeOrgId && !activeOrg && !orgsQuery.isLoading && !orgsQuery.isError;

  // The foreign org's name isn't in `orgs`, so fetch it via the admin lookup
  // (only enabled in this state, which is already super-admin-only).
  const foreignOrgQuery = useQuery({
    queryKey: ["admin", "org", activeOrgId],
    queryFn: () => api.admin.org(activeOrgId!),
    enabled: viewingForeignOrg,
    staleTime: 5 * 60 * 1000,
  });
  const foreignOrgName = foreignOrgQuery.data?.data.name;

  const createMutation = useMutation({
    mutationFn: (name: string) => api.orgs.create(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgs"] });
      setCreateOpen(false);
      setNewOrgName("");
    },
  });

  const navigate = useNavigate();
  const switchOrg = useMutation({
    // Go through the better-auth client (not our raw /set-active POST) so
    // the client-side session cache `useSession()` reads from gets updated.
    // Without this the sidebar kept rendering the *previous* org as active
    // after a switch — visible only to non-admins, since the site-admin
    // bypass branch in authentication-backend swaps the row directly and
    // worked even via the raw POST.
    mutationFn: async (orgId: string) => {
      const result = await authClient.organization.setActive({ organizationId: orgId });
      if (result.error) throw new Error(result.error.message ?? "Failed to switch organization");
      return result.data;
    },
    onSuccess: (_data, orgId) => {
      queryClient.invalidateQueries();
      navigate({ to: "/orgs/$orgId", params: { orgId } });
    },
  });

  // If session exists but has no active org, auto-select the first available one.
  useEffect(() => {
    if (!session || session.session.activeOrganizationId || orgs.length === 0) return;
    switchOrg.mutate(orgs[0].id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session.activeOrganizationId, orgs.length]);

  // Super-admin viewing an org they don't belong to: no switching, just a
  // frozen label in the same switcher shell so it's clear whose workspace this
  // is and that it's a super-admin view.
  if (viewingForeignOrg) {
    return (
      <SidebarMenu className="group-data-[collapsible=icon]:hidden">
        <SidebarMenuItem>
          <SidebarMenuButton
            disabled
            className="h-10 cursor-default border border-dashed border-sidebar-border opacity-100"
          >
            <div className="flex flex-1 flex-col items-start overflow-hidden text-left leading-tight">
              <span className="w-full truncate text-sm font-semibold">
                {foreignOrgName ?? "…"}
              </span>
              <span className="w-full truncate text-[10px] font-normal text-muted-foreground">
                Viewing as super admin
              </span>
            </div>
            <Shield className="ml-auto size-4 shrink-0 opacity-50" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <>
      <SidebarMenu className="group-data-[collapsible=icon]:hidden">
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                className="h-10 border border-sidebar-border data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                {activeOrg?.logo && (
                  <img src={activeOrg.logo} alt="" className="size-6 shrink-0 rounded bg-white object-contain" />
                )}
                <span className="flex-1 truncate text-left text-sm font-semibold">
                  {activeOrg?.name ?? "Select organization"}
                </span>
                <ChevronsUpDown className="ml-auto size-4 opacity-50" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Organizations
              </DropdownMenuLabel>
              {orgsQuery.isError ? (
                <div className="px-2 py-1.5 text-xs text-destructive">Failed to load organizations</div>
              ) : orgs.map((org) => {
                const isActive = org.id === session?.session.activeOrganizationId;
                const isSwitching = switchOrg.isPending && switchOrg.variables === org.id;
                return (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => {
                      if (!isActive) switchOrg.mutate(org.id);
                    }}
                    className="gap-2 p-2"
                  >
                    {org.logo ? (
                      <img src={org.logo} alt="" className="size-5 shrink-0 rounded bg-white object-contain" />
                    ) : (
                      <span aria-hidden className="flex size-5 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-semibold text-muted-foreground">
                        {org.name.trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="flex-1 truncate">{org.name}</span>
                    {isSwitching ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : isActive ? (
                      <Check className="size-4" />
                    ) : null}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 p-2"
                onClick={() => setCreateOpen(true)}
              >
                <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                  <Plus className="size-4" />
                </div>
                <span className="font-medium text-muted-foreground">
                  Add organization
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New organization</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 pt-2">
            <div className="grid gap-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Acme Inc."
                onKeyDown={(e) => e.key === "Enter" && createMutation.mutate(newOrgName.trim())}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => createMutation.mutate(newOrgName.trim())}
                disabled={createMutation.isPending || !newOrgName.trim()}
              >
                {createMutation.isPending ? "Creating…" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NavUser({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const [accountOpen, setAccountOpen] = useState(false);

  async function handleSignOut() {
    try {
      await authClient.signOut();
    } catch (err) {
      console.error("[signOut] failed", err);
    }
    queryClient.clear();
    // Hard reload so the better-auth client cache, sessionStorage, and any
    // in-flight react-query subscriptions all reset cleanly.
    window.location.href = "/login";
  }

  // Prefer initials from the user's name; fall back to the first letter
  // of the email so the avatar always renders something.
  const nameInitials = user.name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const initials = nameInitials || user.email?.[0]?.toUpperCase() || "?";

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="size-8 rounded-lg">
                  <AvatarImage src={user.image ?? undefined} />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="bottom"
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarImage src={user.image ?? undefined} />
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setAccountOpen(true)}>
                <Settings className="size-4 mr-2" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="size-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <AccountSettingsDialog open={accountOpen} onOpenChange={setAccountOpen} />
    </>
  );
}

function NavItems({
  items,
  orgId,
  isActive,
}: {
  items: NavItem[];
  orgId: string | null | undefined;
  isActive: (to: string) => boolean;
}) {
  return (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.to}>
          <SidebarMenuButton asChild isActive={isActive(item.to)} tooltip={item.label}>
            <Link to={item.to} params={{ orgId: orgId ?? "" }}>
              <item.icon />
              <span>{item.label}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}

// Starts expanded for owners, admins and billing admins, collapsed for
// read-only members. Opens whenever the current page is in it, and always
// shows its items in the icon-only sidebar, which has no label to click.
// A toggle holds until the viewer next enters the section from outside.
function OrganizationSection({
  items,
  orgId,
  isActive,
  readOnly,
}: {
  items: NavItem[];
  orgId: string | null | undefined;
  isActive: (to: string) => boolean;
  readOnly: boolean;
}) {
  const { state, isMobile } = useSidebar();
  const iconOnly = state === "collapsed" && !isMobile;
  const inSection = items.some((item) => isActive(item.to));
  const [choice, setChoice] = useState<boolean | null>(null);
  const [prevInSection, setPrevInSection] = useState(inSection);
  if (prevInSection !== inSection) {
    setPrevInSection(inSection);
    if (inSection) setChoice(null);
  }
  const open = iconOnly || (choice ?? (inSection || !readOnly));

  if (items.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setChoice} className="group/organization">
      <SidebarGroup>
        <SidebarGroupLabel
          asChild
          className="cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:pointer-events-none"
        >
          <CollapsibleTrigger>
            Organization
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/organization:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <NavItems items={items} orgId={orgId} isActive={isActive} />
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

export function AppSidebar() {
  const { data: session } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const orgId = session?.session.activeOrganizationId;

  const { data: orgPerms } = useQuery({
    queryKey: ["org-permissions", orgId],
    queryFn: api.org.permissions,
    enabled: !!orgId,
  });
  const permissions = orgPerms?._permissions;

  const isSuperAdmin = useIsSuperAdmin();

  // The session carries the viewer's role in the active org (null for a super
  // admin outside it), so the Organization section can pick its starting state on
  // the first render.
  const orgRole = (session?.session as { activeOrganizationRole?: OrgRole | null } | undefined)?.activeOrganizationRole;

  const visibleNav = NAV_MAIN.filter((item) => {
    if (!item.permissionKey) return true;
    if (typeof item.permissionKey !== "string") {
      return !permissions || item.permissionKey.some((key) => permissions[key] === true);
    }
    if ("exact" in item && item.exact) return permissions?.[item.permissionKey] === true;
    return permissions?.[item.permissionKey] !== false;
  });
  // A viewer whose only Usage tab is Spending limits sees the page titled for it; the entry matches.
  const mainNav = visibleNav
    .filter((item) => !item.orgSection)
    .map((item) => (item.to === "/orgs/$orgId/usage" && limitsOnly(permissions) ? { ...item, label: "Spending limits" } : item));
  const orgSectionNav = visibleNav.filter((item) => item.orgSection);

  // Sidebar links use `/$orgId/...` templates — compare against the current
  // pathname by substituting the live orgId. The Agents tab also lights up
  // for any container detail page (still inside the agents subtree).
  function isActive(to: string) {
    if (!orgId) return false;
    const resolved = to.replace("$orgId", orgId);
    if (to === "/orgs/$orgId") {
      return pathname === resolved || pathname.startsWith(`${resolved}/containers/`);
    }
    return pathname.startsWith(resolved);
  }

  return (
    <Sidebar collapsible="icon" variant="floating">
      <SidebarHeader>
        <div className="flex items-center justify-between gap-2 px-1 pt-1.5 pb-1 group-data-[collapsible=icon]:justify-center">
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
            <BooLogo className="size-6 shrink-0" />
            <span className="text-base font-semibold tracking-tight">Boo</span>
          </div>
          <SidebarTrigger className="shrink-0" />
        </div>
        <OrgSwitcher />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <NavItems items={mainNav} orgId={orgId} isActive={isActive} />
          </SidebarGroupContent>
        </SidebarGroup>
        <OrganizationSection
          items={orgSectionNav}
          orgId={orgId}
          isActive={isActive}
          readOnly={orgRole === "member"}
        />
      </SidebarContent>

      <SidebarFooter>
        {isSuperAdmin && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Super Admin" className="border border-indigo-200/60 bg-indigo-50/50 hover:bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/5 dark:hover:bg-indigo-500/10">
                <a href="/superadmin/organizations" target="_blank" rel="noopener noreferrer">
                  <Shield />
                  <span>Super Admin</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        {session?.user && <NavUser user={session.user} />}
      </SidebarFooter>

    </Sidebar>
  );
}
